import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Plus, Search, UserCheck, UserX, Edit2, X, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const ROLES = ['admin', 'editor', 'viewer'];
const roleColors = { admin: 'bg-red-100 text-red-700', editor: 'bg-blue-100 text-blue-700', viewer: 'bg-gray-100 text-gray-600' };

const UserModal = ({ user, onClose, onSave }) => {
  const [form, setForm] = useState(user || { name: '', email: '', role: 'viewer', department: '', password: 'Welcome@123', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const isEdit = !!user;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEdit) {
      const newPasswordProvided = !!form.newPassword?.trim() || !!form.confirmPassword?.trim();
      if (newPasswordProvided) {
        if (!form.newPassword?.trim() || !form.confirmPassword?.trim()) {
          toast.error('Please enter both password fields');
          return;
        }
        if (form.newPassword !== form.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }
      }
    }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/users/update/${user.id}`, {
          ...form,
          isActive: form.is_active !== false,
          password: form.newPassword?.trim() || '',
        });
        toast.success('User updated');
      } else {
        await api.post('/users/create', form);
        toast.success('User created. Temp password: ' + form.password);
      }
      onSave();
    } catch (e) {}
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit User' : 'Create New User'}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="label">Full Name *</label>
            <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <label className="label">Email *</label>
            <input type="email" className="input" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required disabled={isEdit} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Role *</label>
              <select className="input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Department</label>
              <input className="input" value={form.department || ''} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="e.g. Finance" />
            </div>
          </div>
          {!isEdit && (
            <div>
              <label className="label">Initial Password</label>
              <input className="input" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
          )}
          {isEdit && (
            <div className="flex items-center gap-3">
              <input type="checkbox" id="isActive" checked={form.is_active !== false}
                onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
              <label htmlFor="isActive" className="text-sm text-gray-700">Account Active</label>
            </div>
          )}
          {isEdit && (
            <>
              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  className="input"
                  value={form.newPassword || ''}
                  onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
                  placeholder="Leave blank to keep current password"
                />
              </div>
              <div>
                <label className="label">Confirm Password</label>
                <input
                  type="password"
                  className="input"
                  value={form.confirmPassword || ''}
                  onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                />
              </div>
            </>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [modal, setModal] = useState(null); // null | { user } | { user: null }

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/list', { params: { search, role: roleFilter } });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {}
    setLoading(false);
  }, [search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await api.delete(`/users/delete/${id}`);
      toast.success('User deactivated');
      fetchUsers();
    } catch (e) {}
  };

  return (
    <div className="space-y-6">
      {modal !== null && (
        <UserModal
          user={modal.user}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); fetchUsers(); }}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-gray-500 text-sm">{total} users in workspace</p>
        </div>
        <button onClick={() => setModal({ user: null })} className="btn-primary">
          <Plus className="w-4 h-4" /> Add User
        </button>
      </div>

      <div className="card !p-4 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input w-auto" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
        </select>
      </div>

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['User', 'Role', 'Department', 'Status', 'Last Login', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center font-semibold text-primary-700 text-xs">
                      {u.name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{u.name}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[u.role]}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.department || '—'}</td>
                <td className="px-4 py-3">
                  <span className={u.is_active ? 'badge-active' : 'badge-suspended'}>
                    {u.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  {u.last_login ? format(new Date(u.last_login), 'MMM d, yyyy') : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setModal({ user: u })} className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {u.is_active && (
                      <button onClick={() => handleDeactivate(u.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded">
                        <UserX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersPage;
